import { request } from './client';
import type { OfflinePlayer, BoardOfflinePlayer, CreateOfflinePlayerInput, UpdateOfflinePlayerInput, AddOfflinePlayerToBoardInput, MergeOfflinePlayerInput, MergeOfflinePlayerResponse, SearchOfflinePlayersResult } from '../types';

export const offlinePlayersApi = {
  // ========== User's Offline Players (Profile) ==========

  // Get all offline players owned by the current user
  getMyOfflinePlayers: (): Promise<OfflinePlayer[]> =>
    request('/api/squareup/offline-players'),

  // Search offline players with pagination
  searchMyOfflinePlayers: (params: { query?: string; limit?: number; offset?: number }): Promise<SearchOfflinePlayersResult> => {
    const searchParams = new URLSearchParams();
    if (params.query !== undefined) searchParams.set('query', params.query);
    if (params.limit !== undefined) searchParams.set('limit', params.limit.toString());
    if (params.offset !== undefined) searchParams.set('offset', params.offset.toString());
    return request(`/api/squareup/offline-players?${searchParams.toString()}`);
  },

  // Create a new offline player for the user's profile
  createMyOfflinePlayer: (input: CreateOfflinePlayerInput): Promise<OfflinePlayer> =>
    request('/api/squareup/offline-players', {
      method: 'POST',
      body: input,
    }),

  // Update an offline player owned by the user
  updateMyOfflinePlayer: (playerId: string, input: UpdateOfflinePlayerInput): Promise<OfflinePlayer> =>
    request(`/api/squareup/offline-players/${playerId}`, {
      method: 'PUT',
      body: input,
    }),

  // Delete an offline player owned by the user
  deleteMyOfflinePlayer: (playerId: string): Promise<void> =>
    request(`/api/squareup/offline-players/${playerId}`, {
      method: 'DELETE',
    }),

  // ========== Board's Offline Players ==========

  // Get all offline players on a board (with board-specific data like approvedSquares)
  getBoardOfflinePlayers: (boardId: string): Promise<BoardOfflinePlayer[]> =>
    request(`/api/squareup/boards/${boardId}/offline-players`),

  // Create a new offline player AND add to board
  createBoardOfflinePlayer: (boardId: string, input: CreateOfflinePlayerInput): Promise<OfflinePlayer> =>
    request(`/api/squareup/boards/${boardId}/offline-players`, {
      method: 'POST',
      body: input,
    }),

  // Add an existing offline player to a board
  addToBoard: (boardId: string, playerId: string, input?: AddOfflinePlayerToBoardInput): Promise<void> =>
    request(`/api/squareup/boards/${boardId}/offline-players/${playerId}`, {
      method: 'POST',
      body: input || {},
    }),

  // Remove an offline player from a board (doesn't delete the player)
  removeFromBoard: (boardId: string, playerId: string): Promise<void> =>
    request(`/api/squareup/boards/${boardId}/offline-players/${playerId}`, {
      method: 'DELETE',
    }),

  // Update an offline player's board-specific settings (like approvedSquares)
  updateBoardOfflinePlayer: (boardId: string, playerId: string, input: UpdateOfflinePlayerInput): Promise<BoardOfflinePlayer> =>
    request(`/api/squareup/boards/${boardId}/offline-players/${playerId}`, {
      method: 'PATCH',
      body: input,
    }),

  // Merge an offline player into an online user (transfer squares and payment records)
  mergeToUser: (boardId: string, playerId: string, input: MergeOfflinePlayerInput): Promise<MergeOfflinePlayerResponse> =>
    request(`/api/squareup/boards/${boardId}/offline-players/${playerId}/merge`, {
      method: 'POST',
      body: input,
    }),

  // ========== Legacy API (for backwards compatibility) ==========

  // Get all offline players for a board (alias for getBoardOfflinePlayers)
  list: (boardId: string): Promise<BoardOfflinePlayer[]> =>
    request(`/api/squareup/boards/${boardId}/offline-players`),

  // Create a new offline player (alias for createBoardOfflinePlayer)
  create: (boardId: string, input: CreateOfflinePlayerInput): Promise<OfflinePlayer> =>
    request(`/api/squareup/boards/${boardId}/offline-players`, {
      method: 'POST',
      body: input,
    }),

  // Update an offline player (alias for updateBoardOfflinePlayer)
  update: (boardId: string, playerId: string, input: UpdateOfflinePlayerInput): Promise<BoardOfflinePlayer> =>
    request(`/api/squareup/boards/${boardId}/offline-players/${playerId}`, {
      method: 'PATCH',
      body: input,
    }),

  // Delete an offline player - now removes from board instead of deleting
  delete: (boardId: string, playerId: string): Promise<void> =>
    request(`/api/squareup/boards/${boardId}/offline-players/${playerId}`, {
      method: 'DELETE',
    }),
};
